import { ASSET_PREVIEW_STATUS, ASSET_PREVIEW_TYPE, MEDIA_TYPES } from "@constants";

function sortElementsByResolution(elements) {
    elements.sort(function (aa, bb) {
        const aVideoSetting = aa.videoSetting || { height: 0, width: 0 };
        const bVideoSetting = bb.videoSetting || { height: 0, width: 0 };
        const aResolution = aVideoSetting.height * aVideoSetting.width;
        const bResolution = bVideoSetting.height * bVideoSetting.width;
        if (aResolution < bResolution) return 1;
        if (aResolution > bResolution) return -1;
        return 0;
    });
}

function filterAnalyzableAssets(assets, extensions) {
    return (
        assets &&
        assets.filter(
            (s) =>
                s.mediaType === "VIDEO" &&
                (extensions ? extensions.includes(s.type) : s.type === "MP4" || s.type === "DASH") &&
                (!s.encryptions || s.encryptions.length === 0) &&
                s.elements &&
                s.elements.some((ele) => ele.videoSetting && ele.urls && ele.urls.length > 0),
        )
    );
}

export const findBestAnalyzableVideoElement = function (asset) {
    if (!asset || !asset.elements) return null;

    const videoElements = asset.elements.filter((e) => e.videoSetting && e.urls && e.urls.length > 0);
    if (videoElements.length === 0) return null;

    sortElementsByResolution(videoElements);
    return videoElements[0];
};

/**
 * 에셋에서 COMPLETE 상태의 LOW_QUALITY_VIDEO 프리뷰를 찾는다.
 *
 * @param {Object} asset
 * @returns {Object|null} 프리뷰 객체
 */
export const findLowQualityVideoPreview = function (asset) {
    return (
        asset?.previews?.find(
            (p) => p.type === ASSET_PREVIEW_TYPE.LOW_QUALITY_VIDEO && p.status === ASSET_PREVIEW_STATUS.COMPLETE,
        ) ?? null
    );
};

const findCompleteLowQualityPreview = function (assets) {
    if (!assets || assets.length === 0) return null;
    for (const asset of assets) {
        const preview = findLowQualityVideoPreview(asset);
        if (preview?.accessUrl) {
            return { asset, preview };
        }
    }
    return null;
};

// 기존 소비처 호환용(shape 유지, COMPLETE 필터는 추가됨) — editableSource 이관(DAM-476) 후 제거 예정
export const findLowQualityPreview = function (assets) {
    const found = findCompleteLowQualityPreview(assets);
    if (!found) return null;
    return { asset: found.asset, accessUrl: found.preview.accessUrl, size: found.preview.size };
};

export const findBestAnalyzableAsset = function (assets, extensions) {
    const analyzableAssets = filterAnalyzableAssets(assets, extensions);
    if (!analyzableAssets || analyzableAssets.length === 0) return null;

    analyzableAssets.sort(function (a, b) {
        let aElemnt = null;
        let bElement = null;
        const aVideoElements = a.elements.filter((e) => e.videoSetting && e.urls && e.urls.length > 0);
        if (aVideoElements && aVideoElements.length > 0) {
            sortElementsByResolution(aVideoElements);
            aElemnt = aVideoElements[0];
        }
        const bVideoElements = b.elements.filter((e) => e.videoSetting && e.urls && e.urls.length > 0);
        if (bVideoElements && bVideoElements.length > 0) {
            sortElementsByResolution(bVideoElements);
            bElement = bVideoElements[0];
        }
        const aVideoSetting = aElemnt.videoSetting || { height: 0, width: 0 };
        const bVideoSetting = bElement.videoSetting || { height: 0, width: 0 };
        const aResolution = aVideoSetting.height * aVideoSetting.width;
        const bResolution = bVideoSetting.height * bVideoSetting.width;
        if (aResolution < bResolution) return 1;
        if (aResolution > bResolution) return -1;
        return 0;
    });
    return analyzableAssets;
};

/**
 * 백엔드가 실제로 처리(Transcribe/분석)할 비디오 소스를 찾는다.
 * COMPLETE 상태의 LOW_QUALITY_VIDEO 프리뷰를 우선 채택하고, 없으면 분석 가능한 원본 에셋으로 폴백한다.
 *
 * 프리뷰와 원본이 서로 다른 목록에 담기는 경우(video 상세: 프리뷰는 origins, 원본은 sources)를 위해
 * 원본 탐색 대상만 originalAssets로 분리해 넘길 수 있다.
 *
 * @param {Array} assets - 프리뷰 탐색 대상. originalAssets를 생략하면 원본도 여기서 찾는다
 * @param {Object} [options]
 * @param {string[]} [options.extensions] - 원본 폴백 시 허용할 확장자 (기본: MP4/DASH)
 * @param {Array} [options.originalAssets] - 원본 폴백만 다른 목록에서 찾아야 할 때 지정
 * @returns {{ asset: Object, source: Object, isPreview: boolean }|null}
 *   - asset: source를 보유한 에셋
 *   - source: size/accessUrl을 읽을 대상. 프리뷰면 프리뷰 객체, 원본이면 asset과 동일 객체
 *   - isPreview: 프리뷰가 채택됐으면 true. 호출처는 참조 비교 대신 이 값으로 분기한다
 */
export const findPreviewOrOriginal = function (assets, { extensions, originalAssets } = {}) {
    const found = findCompleteLowQualityPreview(assets);
    if (found) return { asset: found.asset, source: found.preview, isPreview: true };

    const original = findBestAnalyzableAsset(originalAssets ?? assets, extensions)?.[0];
    return original ? { asset: original, source: original, isPreview: false } : null;
};

/**
 * AI 분석이 무엇을 소스로 쓰게 되는지를 나타내는 상태. (DAM-482)
 *
 * 프록시(저용량 사본)가 있으면 그것으로, 없으면 원본으로 분석한다.
 * 원본은 느리고 비싸며 용량이 크면 아예 실패해서, 사용자가 "지금 할지 기다릴지"를
 * 누르기 전에 판단할 수 있어야 한다.
 */
export const ANALYSIS_SOURCE_STATE = {
    PROXY: "PROXY", // 프록시로 분석 - 최적 경로. 확인만 하면 되므로 낮은 무게로 알린다.
    PROXY_IN_PROGRESS: "PROXY_IN_PROGRESS", // 프록시 생성 중 - 기다리면 빨라진다.
    PROXY_FAILED: "PROXY_FAILED", // 프록시 실패 - 원본으로 진행된다.
    ORIGINAL: "ORIGINAL", // 프록시가 없다(사유 불명) - 원본으로 진행된다.
};

/**
 * 자산 1건에서 AI 분석 소스 상태를 계산한다. 표시 전용 파생이며 데이터를 새로 가져오지 않는다.
 *
 * **대상 판별을 `mediaType` 으로 한다** - 자산 상세는 이미지·오디오도 같은 화면을 쓰므로
 * 영상이 아니면 `null` 을 돌려 안내를 감춘다.
 *
 * @param {Object} asset 자산 데이터 (previews · mediaType 사용)
 * @param {Object} [options] `getAnalysisSourceStateForVideo` 와 동일
 * @returns {string|null} ANALYSIS_SOURCE_STATE 값. 영상 자산이 아니면 null
 */
export const getAnalysisSourceState = function (asset, options = {}) {
    if (!asset || asset.mediaType !== MEDIA_TYPES.VIDEO) return null;

    return getAnalysisSourceStateForVideo([asset], options);
};

/**
 * 영상 화면에서 프록시 후보 목록을 보고 소스 상태를 계산한다.
 *
 * **대상 판별을 하지 않는다** - 영상 상세의 AI 탭은 분석 가능한 영상이 있을 때만 열리므로
 * 영상임이 호출부에서 이미 확정돼 있다. 후보 배열(`video.data.origins`)에는 `mediaType` 이
 * 실리지 않을 수 있어, 그것으로 거르면 **원본으로 분석될 영상에 아무 안내도 못 띄운다.**
 *
 * **호출부는 실제 소스가 정해지는 곳과 같은 배열을 넘길 것** - 영상 상세는 `origins`(프록시가 붙는 쪽)이고
 * `sources`가 아니다. 다른 배열을 넘기면 프록시가 있는데도 "원본으로 분석" 이라고 잘못 알린다.
 *
 * PROXY 판정은 실제 채택 함수(findCompleteLowQualityPreview - findPreviewOrOriginal 이
 * 프리뷰를 고르는 그 함수)를 그대로 호출한다 - "프록시로 분석됩니다"가 성립하는 조건과
 * 실제 소스 채택 조건이 같은 함수라서 어긋날 수 없다.
 *
 * @param {Array<Object>} proxyCandidates 프록시가 붙어 있을 수 있는 자산 배열
 * @param {Object} [options]
 * @param {boolean} [options.isProxyEnabled] 프로젝트 설정 `asset.isCreateVideoPreview` 값.
 *        **`false` 면 안내를 통째로 감춘다**(아래 참조). **모르면 넘기지 말 것** - undefined 면 평소대로 안내한다.
 * @returns {string|null} ANALYSIS_SOURCE_STATE 값. 설정이 꺼져 있으면 null(안내 없음)
 */
export const getAnalysisSourceStateForVideo = function (proxyCandidates, { isProxyEnabled } = {}) {
    // 프로젝트가 프록시를 만들지 않기로 했다면 프록시 얘기 자체가 무의미하다.
    // 매번 "원본으로 분석됩니다"를 띄우면 고칠 수 없는 잔소리가 된다 - 통째로 침묵한다.
    // 대신 그 사정은 프로젝트 설정 화면이 설명한다(이 모듈 밖).
    if (isProxyEnabled === false) return null;

    if (findCompleteLowQualityPreview(proxyCandidates ?? [])) return ANALYSIS_SOURCE_STATE.PROXY;

    const proxies = (proxyCandidates ?? [])
        .filter(Boolean)
        .flatMap((asset) =>
            (asset.previews ?? []).filter((preview) => preview?.type === ASSET_PREVIEW_TYPE.LOW_QUALITY_VIDEO),
        );

    if (proxies.some((preview) => preview?.status === ASSET_PREVIEW_STATUS.IN_PROGRESS)) {
        return ANALYSIS_SOURCE_STATE.PROXY_IN_PROGRESS;
    }
    if (proxies.some((preview) => preview?.status === ASSET_PREVIEW_STATUS.ERROR)) {
        return ANALYSIS_SOURCE_STATE.PROXY_FAILED;
    }

    // 프록시 레코드가 있으나 만들 수 없는 자산(NOT_SUPPORTED)이거나, 아예 없다.
    // 어느 쪽이든 지금 분석하면 원본으로 간다는 사실은 같다.
    return ANALYSIS_SOURCE_STATE.ORIGINAL;
};

/**
 * 영상 편집/AI 워크스페이스 다이얼로그(EditVideo)에 넘길 소스를 만든다.
 *
 * 재생은 프리뷰(프록시)로, 저장(인코딩)은 원본으로 이뤄진다:
 * - accessUrls/url  → 프리뷰가 있으면 프리뷰. 원본은 faststart 가 아닌 경우가 많아 재생 시작이 느리다.
 * - originalUrls    → 항상 에셋의 것(프리뷰로 덮이지 않음). EditVideo 의 저장 경로가 이 값을 쓴다.
 * - duration/elements → 에셋(원본) 메타데이터. 타임라인 좌표와 결과물 timecode 의 기준이다.
 *
 * @param {Array} assets - 소스 탐색 대상 에셋 목록
 * @param {Object} [options]
 * @param {string[]} [options.extensions] - 원본 폴백 시 허용할 확장자
 * @returns {Object|null} EditVideo 의 props 로 넘길 소스. 재생 가능한 소스가 없으면 null
 *   - `isPreview` — 프리뷰(프록시)를 채택했는지
 *   - `previewVideoSetting` — 프리뷰일 때만. 라벨 표시용 `{width, height}`(프록시가 주는 값은 이것과 size 뿐)
 */
export const createEditableSource = function (assets, { extensions } = {}) {
    const sourceSelection = findPreviewOrOriginal(assets, { extensions });
    if (!sourceSelection) return null;

    const { asset, source, isPreview } = sourceSelection;
    const accessUrls = isPreview ? [source.accessUrl] : source.accessUrls ?? [];
    const url = accessUrls?.[0];

    if (isPreview) {
        return {
            ...asset,
            size: source.size,
            url,
            accessUrls,
            isPreview: true,
            previewVideoSetting: { width: source.width, height: source.height },
        };
    }
    return { ...source, url, accessUrls, isPreview: false };
};
